import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useReveal from "@/hooks/useReveal";
import CopyableEmail from "@/components/CopyableEmail";
import useSEO from "@/hooks/useSEO";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, Clock, MapPin, Mail, Calendar, ArrowUpRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CALENDLY_URL = "https://calendly.com/archanaura8/30min";

const PROJECT_TYPES = [
  "New property opening",
  "Rebrand",
  "Refresh",
  "Multi-property rollout",
  "Other",
];

const INITIAL = {
  full_name: "",
  company: "",
  role: "",
  email: "",
  phone: "",
  project_type: "",
  brief: "",
};

export default function Consultation() {
  useSEO({
    title: "Book a Design Consultation · Auraa Designs",
    description:
      "Request a hospitality uniform design consultation with Archana Panda. Response within 24 hours.",
    path: "/consultation",
  });

  return (
    <div data-testid="consultation-page" className="pt-28 md:pt-32 pb-28 md:pb-40">
      <div className="container-luxury">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 xl:gap-24 items-start">
          <EditorialColumn />
          <FormColumn />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────── LEFT */
function EditorialColumn() {
  const ref = useReveal();
  return (
    <aside ref={ref} className="reveal lg:col-span-5 lg:sticky lg:top-28 lg:self-start">
      <div className="flex items-center gap-4 mb-7">
        <span className="h-px w-10 bg-oxblood" />
        <span className="text-[11px] uppercase tracking-[0.32em] text-oxblood font-medium">
          Consultation
        </span>
      </div>

      <h1 className="font-serif text-[40px] sm:text-[48px] md:text-[56px] lg:text-[60px] leading-[1.04] tracking-[-0.02em] text-espresso text-balance">
        Start a hospitality
        <br />
        design <em className="italic text-oxblood">consultation.</em>
      </h1>

      <p className="mt-7 max-w-md text-[15px] md:text-[16px] leading-[1.75] text-espresso-soft font-light">
        Whether you're opening a new property, rebranding a hospitality
        experience, or rolling out uniforms nationally, Auraa helps design and
        execute hospitality uniform systems aligned to your guest experience.
      </p>

      <div className="mt-10 border-t border-borderwarm pt-7 space-y-5">
        <MetaRow icon={Clock} label="Response" value="Within 24 hours" />
        <MetaRow icon={MapPin} label="Atelier" value="Bhubaneswar · Pan-India rollout" />
        <MetaRow
          icon={Mail}
          label="Direct"
          value={<CopyableEmail testid="consultation-email" />}
        />
      </div>

      {/* Calendly direct link */}
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="calendly-direct"
        className="group mt-8 inline-flex items-center justify-between gap-4 w-full border border-borderwarm px-5 py-4 bg-bone hover:border-oxblood transition-all duration-500"
      >
        <span className="flex items-center gap-3">
          <Calendar size={15} className="text-oxblood" />
          <span className="flex flex-col items-start">
            <span className="text-[10px] uppercase tracking-[0.28em] text-espresso-soft">Or schedule directly</span>
            <span className="text-[13px] text-espresso group-hover:text-oxblood transition-colors">30-min atelier conversation</span>
          </span>
        </span>
        <ArrowUpRight size={14} className="text-espresso group-hover:text-oxblood transition-all duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </aside>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="grid grid-cols-12 gap-4 items-start">
      <div className="col-span-1 pt-1">
        <Icon size={14} className="text-oxblood" />
      </div>
      <div className="col-span-4">
        <div className="text-[10px] uppercase tracking-[0.28em] text-espresso-soft pt-0.5">
          {label}
        </div>
      </div>
      <div className="col-span-7 text-[14px] text-espresso font-light leading-relaxed">
        {value}
      </div>
    </div>
  );
}

/* ────────────────────────────── RIGHT (form) */
function FormColumn() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);
  const ref = useReveal();
  const navigate = useNavigate();

  const update = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    Object.entries(form).forEach(([k, v]) => {
      if (!String(v).trim()) e[k] = "Required";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setDone(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        ref={ref}
        data-testid="consultation-success"
        className="reveal lg:col-span-7 bg-bone border border-borderwarm p-10 md:p-16 lg:p-20 min-h-[480px] flex flex-col justify-center"
      >
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-10 bg-oxblood" />
          <span className="text-[11px] uppercase tracking-[0.32em] text-oxblood font-medium">
            Request Received
          </span>
        </div>
        <h2 className="font-serif text-[34px] md:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.015em] text-espresso text-balance">
          Thank you{form.full_name && <>, <em className="italic text-oxblood">{form.full_name.split(" ")[0]}</em></>}.
          <br />
          Archana will respond within 24 hours.
        </h2>
        <p className="mt-6 max-w-xl text-[15px] md:text-[16px] leading-relaxed text-espresso-soft font-light">
          We've received your consultation request. A note has been sent to the
          atelier — Archana personally reviews every enquiry.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <button
            type="button"
            onClick={() => navigate("/projects")}
            className="btn-secondary"
            data-testid="success-view-projects"
          >
            View Selected Projects
          </button>
          <button
            type="button"
            onClick={() => {
              setForm(INITIAL);
              setDone(false);
            }}
            className="text-[11px] uppercase tracking-[0.28em] text-espresso hover:text-oxblood transition-colors duration-500 self-center"
            data-testid="success-new-request"
          >
            Submit another request
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      ref={ref}
      onSubmit={onSubmit}
      data-testid="consultation-form"
      className="reveal lg:col-span-7 bg-bone border border-borderwarm p-7 md:p-12 lg:p-14"
      noValidate
    >
      <div className="flex items-baseline justify-between pb-5 border-b border-borderwarm mb-10">
        <div className="flex items-center gap-4">
          <span className="font-serif italic text-[18px] text-champagne-dark">A</span>
          <span className="text-[11px] uppercase tracking-[0.32em] text-oxblood font-medium">
            Consultation Request
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.28em] text-espresso-soft hidden sm:inline">
          7 fields
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-9">
        <Field label="Full Name" required error={errors.full_name}>
          <input
            type="text"
            className="luxury-input"
            value={form.full_name}
            onChange={update("full_name")}
            data-testid="input-full-name"
            placeholder="Your full name"
          />
        </Field>

        <Field label="Company / Property Name" required error={errors.company}>
          <input
            type="text"
            className="luxury-input"
            value={form.company}
            onChange={update("company")}
            data-testid="input-company"
            placeholder="e.g. The Sablon"
          />
        </Field>

        <Field label="Role / Designation" required error={errors.role}>
          <input
            type="text"
            className="luxury-input"
            value={form.role}
            onChange={update("role")}
            data-testid="input-role"
            placeholder="e.g. General Manager"
          />
        </Field>

        <Field label="Email" required error={errors.email}>
          <input
            type="email"
            className="luxury-input"
            value={form.email}
            onChange={update("email")}
            data-testid="input-email"
            placeholder="name@property.com"
          />
        </Field>

        <Field label="Phone / WhatsApp" required error={errors.phone}>
          <input
            type="tel"
            className="luxury-input"
            value={form.phone}
            onChange={update("phone")}
            data-testid="input-phone"
            placeholder="+91"
          />
        </Field>

        <Field label="Project Type" required error={errors.project_type}>
          <Select value={form.project_type} onValueChange={(v) => update("project_type")(v)}>
            <SelectTrigger
              data-testid="select-project-type"
              className="!rounded-none !bg-transparent !border-0 !border-b !border-borderwarm !h-auto !py-4 !px-0 !text-base text-espresso font-light focus:!ring-0 focus:!border-oxblood focus:!outline-none data-[placeholder]:text-espresso-soft/60"
            >
              <SelectValue placeholder="Select project type" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-borderwarm bg-bone">
              {PROJECT_TYPES.map((o) => (
                <SelectItem
                  key={o}
                  value={o}
                  className="rounded-none text-espresso font-light focus:bg-bone-200 focus:text-oxblood"
                >
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Brief about your requirement" required error={errors.brief} span={2}>
          <textarea
            rows={6}
            className="luxury-input resize-none"
            value={form.brief}
            onChange={update("brief")}
            data-testid="input-brief"
            placeholder="Property type, opening timeline, departments, rebrand direction, references — anything that helps us prepare."
          />
        </Field>
      </div>

      <div className="mt-12 pt-7 border-t border-borderwarm flex flex-col sm:flex-row sm:items-center gap-5 sm:justify-between">
        <p className="text-[12px] text-espresso-soft font-light max-w-md leading-relaxed">
          By submitting, you agree to be contacted about your consultation
          request. We treat your information with discretion.
        </p>
        <button
          type="submit"
          disabled={submitting}
          data-testid="submit-consultation"
          className="btn-primary group disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Submitting…" : "Request Consultation"}
          <ArrowRight
            size={16}
            className="transition-transform duration-500 group-hover:translate-x-1"
          />
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, error, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "md:col-span-2" : ""}`}>
      <span className="flex items-center gap-2 mb-3">
        <span className="text-[11px] uppercase tracking-[0.28em] text-espresso font-medium">
          {label}
        </span>
        {required && <span className="text-[9px] text-oxblood">●</span>}
      </span>
      {children}
      {error && (
        <span className="block mt-2 text-[11px] uppercase tracking-[0.22em] text-oxblood">
          {error}
        </span>
      )}
    </label>
  );
}
